import { createEffect, createMemo, Show, untrack } from "solid-js"
import { createStore } from "solid-js/store"
import { useLocation, useNavigate, useParams } from "@solidjs/router"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { Icon } from "@openanalyst-ai/ui/icon"
import { Button } from "@openanalyst-ai/ui/button"
import { Tooltip, TooltipKeybind } from "@openanalyst-ai/ui/tooltip"
import { useTheme } from "@openanalyst-ai/ui/theme/context"

import { useLayout } from "@/context/layout"
import { usePlatform } from "@/context/platform"
import { useCommand } from "@/context/command"
import { useLanguage } from "@/context/language"
import { useSettings } from "@/context/settings"
import { decode64 } from "@/utils/base64"
import { applyPath, backPath, forwardPath } from "./titlebar-history"
import { titlebarChannelBadgeLabel } from "./titlebar-channel"
import { TitlebarProductSuiteHandle } from "./titlebar-product-suite-handle"

type TauriDesktopWindow = {
  startDragging?: () => Promise<void>
  toggleMaximize?: () => Promise<void>
}

type TauriThemeWindow = {
  setTheme?: (theme?: "light" | "dark" | null) => Promise<void>
}

type TauriApi = {
  window?: {
    getCurrentWindow?: () => TauriDesktopWindow
  }
  webviewWindow?: {
    getCurrentWebviewWindow?: () => TauriThemeWindow
  }
}

const tauriApi = () => (window as unknown as { __TAURI__?: TauriApi }).__TAURI__
const currentDesktopWindow = () => tauriApi()?.window?.getCurrentWindow?.()
const currentThemeWindow = () => tauriApi()?.webviewWindow?.getCurrentWebviewWindow?.()

export function Titlebar() {
  const layout = useLayout()
  const platform = usePlatform()
  const command = useCommand()
  const language = useLanguage()
  const settings = useSettings()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  const mac = createMemo(() => platform.platform === "desktop" && platform.os === "macos")
  const windows = createMemo(() => platform.platform === "desktop" && platform.os === "windows")
  const web = createMemo(() => platform.platform === "web")
  const zoom = () => platform.webviewZoom?.() ?? 1
  const minHeight = () => (mac() ? `${44 / zoom()}px` : undefined)

  const [history, setHistory] = createStore({
    stack: [] as string[],
    index: 0,
    action: undefined as "back" | "forward" | undefined,
  })

  const path = () => `${location.pathname}${location.search}${location.hash}`
  const creating = createMemo(() => {
    if (!params.dir) return false
    if (params.id) return false
    const parts = location.pathname.replace(/\/+$/, "").split("/")
    return parts.at(-1) === "session"
  })

  createEffect(() => {
    const current = path()

    untrack(() => {
      const next = applyPath(history, current)
      if (next === history) return
      setHistory(next)
    })
  })

  const canBack = createMemo(() => history.index > 0)
  const canForward = createMemo(() => history.index < history.stack.length - 1)
  const hasProjects = createMemo(() => layout.projects.list().length > 0)
  const sidebarAvailable = createMemo(() => !!params.dir)
  const nav = createMemo(() => import.meta.env.VITE_OPENANALYST_CHANNEL !== "beta" || settings.general.showNavigation())
  const projectPath = createMemo(() => decode64(params.dir) ?? "")
  const channelBadgeLabel = createMemo(() =>
    titlebarChannelBadgeLabel({
      VITE_OPENANALYST_CHANNEL: import.meta.env.VITE_OPENANALYST_CHANNEL,
      VITE_OPENANALYST_HOSTED_RUNTIME: import.meta.env.VITE_OPENANALYST_HOSTED_RUNTIME,
    }),
  )

  const back = () => {
    const next = backPath(history)
    if (!next) return
    setHistory(next.state)
    navigate(next.to)
  }

  const forward = () => {
    const next = forwardPath(history)
    if (!next) return
    setHistory(next.state)
    navigate(next.to)
  }

  command.register(() => [
    {
      id: "common.goBack",
      title: language.t("common.goBack"),
      category: language.t("command.category.view"),
      keybind: "mod+[",
      onSelect: back,
    },
    {
      id: "common.goForward",
      title: language.t("common.goForward"),
      category: language.t("command.category.view"),
      keybind: "mod+]",
      onSelect: forward,
    },
  ])

  const getWin = () => {
    if (platform.platform !== "desktop") return
    return currentDesktopWindow()
  }

  createEffect(() => {
    if (platform.platform !== "desktop") return

    const scheme = theme.colorScheme()
    const value = scheme === "system" ? null : scheme

    const win = currentThemeWindow()
    if (!win?.setTheme) return

    void win.setTheme(value).catch(() => undefined)
  })

  const interactive = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false

    const selector =
      "button, a, input, textarea, select, option, [role='button'], [role='menuitem'], [contenteditable='true'], [contenteditable='']"

    return !!target.closest(selector)
  }

  const drag = (e: MouseEvent) => {
    if (platform.platform !== "desktop") return
    if (e.buttons !== 1) return
    if (interactive(e.target)) return

    const win = getWin()
    if (!win?.startDragging) return

    e.preventDefault()
    void win.startDragging().catch(() => undefined)
  }

  const maximize = (e: MouseEvent) => {
    if (platform.platform !== "desktop") return
    if (interactive(e.target)) return
    if (e.target instanceof Element && e.target.closest("[data-tauri-decorum-tb]")) return

    const win = getWin()
    if (!win?.toggleMaximize) return

    e.preventDefault()
    void win.toggleMaximize().catch(() => undefined)
  }

  return (
    <header
      class="h-11 shrink-0 bg-background-base relative grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center"
      style={{ "min-height": minHeight() }}
      data-tauri-drag-region
      onMouseDown={drag}
      onDblClick={maximize}
    >
      <div
        classList={{
          "flex items-center min-w-0": true,
          "pl-2": !mac(),
        }}
      >
        <Show when={mac()}>
          <div class="h-full shrink-0" style={{ width: `${72 / zoom()}px` }} />
          <div class="lg:hidden w-10 shrink-0 flex items-center justify-center">
            <IconButton
              icon="menu"
              variant="ghost"
              class="titlebar-icon rounded-md"
              disabled={!sidebarAvailable()}
              onClick={() => {
                if (!sidebarAvailable()) return
                layout.mobileSidebar.toggle()
              }}
              aria-label={language.t("sidebar.menu.toggle")}
              aria-expanded={sidebarAvailable() && layout.mobileSidebar.opened()}
            />
          </div>
        </Show>
        <Show when={!mac()}>
          <div class="lg:hidden w-[48px] shrink-0 flex items-center justify-center">
            <IconButton
              icon="menu"
              variant="ghost"
              class="titlebar-icon rounded-md"
              disabled={!sidebarAvailable()}
              onClick={() => {
                if (!sidebarAvailable()) return
                layout.mobileSidebar.toggle()
              }}
              aria-label={language.t("sidebar.menu.toggle")}
              aria-expanded={sidebarAvailable() && layout.mobileSidebar.opened()}
            />
          </div>
        </Show>
        <div class="flex items-center gap-1 shrink-0">
          {/* Sidebar toggle removed from the titlebar per design; the sidebar is still
              toggleable via its keyboard shortcut (command "sidebar.toggle"). */}
          <div class="hidden lg:flex items-center shrink-0">
            <Show when={params.dir}>
              <div
                class="flex items-center shrink-0 w-8"
                aria-hidden={layout.sidebar.opened() ? "true" : undefined}
              >
                <div
                  class="transition-opacity"
                  classList={{
                    "opacity-100 duration-120 ease-out": !layout.sidebar.opened(),
                    "opacity-0 duration-120 ease-in delay-0 pointer-events-none": layout.sidebar.opened(),
                  }}
                >
                  <TooltipKeybind
                    placement="bottom"
                    title={language.t("command.session.new")}
                    keybind={command.keybind("session.new")}
                    openDelay={2000}
                  >
                    <Button
                      variant="ghost"
                      icon={creating() ? "new-session-active" : "new-session"}
                      class="titlebar-icon w-8 h-6 p-0 box-border"
                      disabled={layout.sidebar.opened()}
                      tabIndex={layout.sidebar.opened() ? -1 : undefined}
                      onClick={() => {
                        if (!params.dir) return
                        navigate(`/${params.dir}/session`)
                      }}
                      aria-label={language.t("command.session.new")}
                      aria-current={creating() ? "page" : undefined}
                    />
                  </TooltipKeybind>
                </div>
              </div>
            </Show>
            <div
              class="flex items-center shrink-0"
              classList={{
                "-translate-x-[36px]": layout.sidebar.opened() && !!params.dir,
                "duration-180 ease-out": !layout.sidebar.opened(),
                "duration-180 ease-in": layout.sidebar.opened(),
              }}
            >
              <Show when={hasProjects() && nav()}>
                <div class="flex items-center gap-0 transition-transform">
                  <Tooltip placement="bottom" value={language.t("common.goBack")} openDelay={2000}>
                    <Button
                      variant="ghost"
                      icon="chevron-left"
                      class="titlebar-icon w-6 h-6 p-0 box-border"
                      disabled={!canBack()}
                      onClick={back}
                      aria-label={language.t("common.goBack")}
                    />
                  </Tooltip>
                  <Tooltip placement="bottom" value={language.t("common.goForward")} openDelay={2000}>
                    <Button
                      variant="ghost"
                      icon="chevron-right"
                      class="titlebar-icon w-6 h-6 p-0 box-border"
                      disabled={!canForward()}
                      onClick={forward}
                      aria-label={language.t("common.goForward")}
                    />
                  </Tooltip>
                </div>
              </Show>
              <TitlebarProductSuiteHandle projectPath={projectPath()} />
              <div id="openanalyst-titlebar-left" class="flex items-center gap-3 min-w-0 px-2" />
              <Show when={channelBadgeLabel()}>
                <div class="bg-icon-interactive-base text-[#FFF] font-medium px-2 rounded-sm uppercase font-mono">
                  {channelBadgeLabel()}
                </div>
              </Show>
            </div>
          </div>
        </div>
      </div>

      <div class="min-w-0 flex items-center justify-center overflow-hidden pointer-events-none">
        <div class="pointer-events-auto min-w-0 flex items-center justify-center gap-2 w-fit max-w-full overflow-hidden">
          <div id="openanalyst-titlebar-center" class="min-w-0 flex justify-center w-fit max-w-full overflow-hidden" />
        </div>
      </div>

      <div
        classList={{
          "flex items-center min-w-0 justify-end overflow-hidden": true,
          "pr-2": !windows(),
        }}
        data-tauri-drag-region
        onMouseDown={drag}
      >
        <div id="openanalyst-titlebar-right" class="flex min-w-0 items-center gap-1 justify-end overflow-hidden" />
        <Show when={windows()}>
          {!tauriApi() && <div class="w-36 shrink-0" />}
          <div data-tauri-decorum-tb class="flex flex-row" />
        </Show>
      </div>
    </header>
  )
}
